import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Button, Select, Input, IconButton } from '../ui';
import { PencilIcon, TrashIcon, PlusIcon, UserCircleIcon } from '@heroicons/react/24/outline';
import { ChartBarIcon } from '@heroicons/react/24/solid';
import api from '../../services/api';
import { useDatabases } from '../../hooks/useDatabases';

const MINISTRIES = [
  'Office of the Prime Minister',
  'Office of the Deputy Prime Minister and Ministry for Foreign Affairs and Tourism',
  'Ministry for Culture, Lands and Local Government',
  'Ministry for Social Policy and Children\'s Rights',
  'Ministry for Agriculture, Fisheries and Animal Rights',
  'Ministry for the Economy, Enterprise and Strategic Projects',
  'Ministry for Inclusion and the Voluntary Sector',
  'Ministry for Gozo and Planning',
  'Ministry for Home Affairs, Security and Employment',
  'Ministry for the Environment, Energy and Public Cleanliness',
  'Ministry for Finance',
  'Ministry for Education, Sport, Youth, Research and Innovation',
  'Ministry for Transport, Infrastructure and Public Works'
];

const ORG_SIZES = [
  { value: 'small',      label: 'Small (1–50)' },
  { value: 'medium',     label: 'Medium (51–250)' },
  { value: 'large',      label: 'Large (251–1,000)' },
  { value: 'enterprise', label: 'Enterprise (1,000+)' },
];

const CONSULTING_STATUS_OPTIONS = [
  { value: 'active',    label: 'Active' },
  { value: 'completed', label: 'Completed' },
  { value: 'paused',    label: 'Paused' },
];

const emptyConsulting = () => ({
  consultingStatus: 'active',
  organisationSize: '',
  contacts: [{ name: '', role: '', email: '' }],
});

export default function CompanyDetails({
  company,
  villages,
  industries,
  onUpdate,
  onDelete,
  mode, // 'view', 'edit', 'new'
  onCancel,
  onViewInsights
}) {
  const navigate = useNavigate();
  const { data: databases = [] } = useDatabases();
  const [formData, setFormData] = useState({
    name: '',
    village: '',
    region: '',
    industry: '',
    isGovernment: false,
    ministry: ''
  });
  const [consulting, setConsulting] = useState(emptyConsulting());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [customIndustry, setCustomIndustry] = useState('');
  const [customMinistry, setCustomMinistry] = useState('');
  const [showCustomIndustry, setShowCustomIndustry] = useState(false);
  const [showCustomMinistry, setShowCustomMinistry] = useState(false);

  useEffect(() => {
    if (company) {
      const industryInList = industries.includes(company.industry);
      const ministryInList = MINISTRIES.includes(company.ministry);

      setFormData({
        name: company.name || '',
        village: company.village || '',
        region: company.region || '',
        industry: industryInList ? company.industry : '__custom__',
        isGovernment: company.isGovernment || false,
        ministry: ministryInList ? company.ministry : '__custom__'
      });

      if (!industryInList && company.industry) {
        setCustomIndustry(company.industry);
        setShowCustomIndustry(true);
      } else {
        setCustomIndustry('');
        setShowCustomIndustry(false);
      }

      if (!ministryInList && company.ministry) {
        setCustomMinistry(company.ministry);
        setShowCustomMinistry(true);
      } else {
        setCustomMinistry('');
        setShowCustomMinistry(false);
      }

      // Load consulting fields from company record
      setConsulting({
        consultingStatus: company.consultingStatus || 'active',
        organisationSize: company.organisationSize || '',
        contacts: company.contacts?.length ? company.contacts : [{ name: '', role: '', email: '' }],
      });
    } else if (mode === 'new') {
      setFormData({ name: '', village: '', region: '', industry: '', isGovernment: false, ministry: '' });
      setCustomIndustry('');
      setCustomMinistry('');
      setShowCustomIndustry(false);
      setShowCustomMinistry(false);
      setConsulting(emptyConsulting());
    }

    if (mode === 'view') {
      setShowCustomIndustry(false);
      setShowCustomMinistry(false);
    }
  }, [company, mode, industries]);

  const handleVillageChange = async (village) => {
    setFormData({ ...formData, village, region: '' });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      const payload = {
        name: formData.name,
        village: formData.village,
        industry: formData.industry === '__custom__' ? customIndustry : formData.industry,
        isGovernment: formData.isGovernment,
        ministry: formData.isGovernment
          ? (formData.ministry === '__custom__' ? customMinistry : formData.ministry)
          : null
      };

      let savedCompany;
      if (mode === 'new') {
        const response = await api.post('/cms/companies', payload);
        savedCompany = response.data;
      } else {
        const response = await api.put(`/cms/companies/${company.id}`, payload);
        savedCompany = response.data;
      }

      // Handle consulting fields — all companies are consulting clients
      const consultingPayload = {
        consultingStatus: consulting.consultingStatus,
        organisationSize: consulting.organisationSize || null,
        contacts: consulting.contacts.filter(c => c.name.trim()),
      };

      const wasAlreadyClient = !!company?.consultingStatus;
      if (!wasAlreadyClient || mode === 'new') {
        // First time activation — creates default phases
        await api.post('/clients', { companyId: savedCompany.id, ...consultingPayload });
      } else {
        await api.put(`/clients/${savedCompany.id}/consulting`, consultingPayload);
      }

      onUpdate(savedCompany);
    } catch (err) {
      console.error('Error saving company:', err);
      setError(err.response?.data?.error || 'Failed to save company');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Are you sure you want to delete ${company.name}?`)) return;
    try {
      setSaving(true);
      setError(null);
      await api.delete(`/cms/companies/${company.id}`);
      onDelete(company.id);
    } catch (err) {
      console.error('Error deleting company:', err);
      setError(err.response?.data?.error || 'Failed to delete company');
      setSaving(false);
    }
  };

  // Consulting helpers
  const setC = (key, val) => setConsulting(c => ({ ...c, [key]: val }));
  const addContact = () => setC('contacts', [...consulting.contacts, { name: '', role: '', email: '' }]);
  const removeContact = (i) => setC('contacts', consulting.contacts.filter((_, idx) => idx !== i));
  const updateContact = (i, key, val) =>
    setC('contacts', consulting.contacts.map((c, idx) => idx === i ? { ...c, [key]: val } : c));

  if (!company && mode !== 'new') {
    return (
      <Card>
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <p className="text-text-muted-light dark:text-text-muted">Select a company to view details</p>
        </div>
      </Card>
    );
  }

  const isEditing = mode === 'edit' || mode === 'new';

  return (
    <Card>
      <div className="flex items-center gap-3 mb-4">
        <h2 className="text-2xl font-bold text-text-primary-light dark:text-text-primary">
          {mode === 'new' ? 'New Company' : company?.name}
        </h2>

        <div className="flex gap-2">
          {isEditing ? (
            <>
              <Button
                variant="primary"
                onClick={handleSave}
                disabled={
                  saving ||
                  !formData.name ||
                  !formData.village ||
                  (showCustomIndustry && !customIndustry.trim()) ||
                  (showCustomMinistry && !customMinistry.trim())
                }
              >
                {saving ? 'Saving...' : 'Save'}
              </Button>
              <Button variant="secondary" onClick={onCancel} disabled={saving}>
                Cancel
              </Button>
            </>
          ) : (
            <>
              <IconButton
                icon={PencilIcon}
                label="Edit Company"
                variant="primary"
                size="md"
                display="icon"
                onClick={() => onUpdate(company, 'edit')}
              />
              {onViewInsights && (
                <IconButton
                  icon={ChartBarIcon}
                  label="View Insights"
                  variant="secondary"
                  size="md"
                  display="icon"
                  onClick={() => navigate(`/insights?view=company&id=${company.id}`)}
                />
              )}
              {company.consultingStatus && (
                <IconButton
                  icon={UserCircleIcon}
                  label="View Client"
                  variant="secondary"
                  size="md"
                  display="icon"
                  onClick={() => navigate(`/clients/${company.id}`)}
                />
              )}
              {company.responseCount === 0 && (
                <IconButton
                  icon={TrashIcon}
                  label="Delete Company"
                  variant="danger"
                  size="md"
                  display="icon"
                  onClick={handleDelete}
                  disabled={saving}
                />
              )}
            </>
          )}
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
        </div>
      )}

      <div className="space-y-4">
        {/* Name (only shown when editing) */}
        {isEditing && (
          <div>
            <label className="block text-sm font-medium text-text-secondary-light dark:text-text-secondary mb-1">Name</label>
            <Input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Company name"
            />
          </div>
        )}

        {/* Village */}
        <div>
          <label className="block text-sm font-medium text-text-secondary-light dark:text-text-secondary mb-1">Village</label>
          {isEditing ? (
            <Select value={formData.village} onChange={(e) => handleVillageChange(e.target.value)}>
              <option value="">Select village...</option>
              {villages.map(village => (
                <option key={village} value={village}>{village}</option>
              ))}
            </Select>
          ) : (
            <div className="text-text-primary-light dark:text-text-primary">{company?.village}</div>
          )}
        </div>

        {/* Region (read-only) */}
        <div>
          <label className="block text-sm font-medium text-text-secondary-light dark:text-text-secondary mb-1">Region (auto-inferred)</label>
          {isEditing ? (
            <div className="px-3 py-2 bg-white dark:bg-background-gray border border-border-light dark:border-border rounded-lg text-text-primary-light dark:text-text-primary opacity-75">
              {formData.region || 'Select village first'}
            </div>
          ) : (
            <div className="text-text-primary-light dark:text-text-primary">{company?.region}</div>
          )}
        </div>

        {/* Industry */}
        <div>
          <label className="block text-sm font-medium text-text-secondary-light dark:text-text-secondary mb-1">Industry</label>
          {isEditing ? (
            <>
              <Select
                value={formData.industry}
                onChange={(e) => {
                  const value = e.target.value;
                  setFormData({ ...formData, industry: value });
                  setShowCustomIndustry(value === '__custom__');
                  if (value !== '__custom__') setCustomIndustry('');
                }}
              >
                <option value="">Select industry...</option>
                {industries.map(industry => (
                  <option key={industry} value={industry}>{industry}</option>
                ))}
                <option value="__custom__">+ Add new industry...</option>
              </Select>
              {showCustomIndustry && (
                <Input
                  type="text"
                  value={customIndustry}
                  onChange={(e) => setCustomIndustry(e.target.value)}
                  placeholder="Enter custom industry"
                  className="mt-2"
                />
              )}
            </>
          ) : (
            <div className="text-text-primary-light dark:text-text-primary">{company?.industry}</div>
          )}
        </div>

        {/* Government checkbox */}
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="isGovernment"
            checked={formData.isGovernment}
            onChange={(e) => setFormData({ ...formData, isGovernment: e.target.checked })}
            disabled={!isEditing}
            className="w-4 h-4 text-brand-primary bg-white dark:bg-background-gray border border-border-light dark:border-border rounded focus:ring-2 focus:ring-brand-primary disabled:opacity-50 disabled:cursor-not-allowed"
          />
          <label htmlFor="isGovernment" className="text-sm font-medium text-text-secondary-light dark:text-text-secondary">
            Government Organization
          </label>
        </div>

        {/* Ministry */}
        {formData.isGovernment && (
          <div>
            <label className="block text-sm font-medium text-text-secondary-light dark:text-text-secondary mb-1">Ministry</label>
            {isEditing ? (
              <>
                <Select
                  value={formData.ministry}
                  onChange={(e) => {
                    const value = e.target.value;
                    setFormData({ ...formData, ministry: value });
                    setShowCustomMinistry(value === '__custom__');
                    if (value !== '__custom__') setCustomMinistry('');
                  }}
                >
                  <option value="">Select Ministry</option>
                  {MINISTRIES.map(ministry => (
                    <option key={ministry} value={ministry}>{ministry}</option>
                  ))}
                  <option value="__custom__">+ Add new ministry...</option>
                </Select>
                {showCustomMinistry && (
                  <Input
                    type="text"
                    value={customMinistry}
                    onChange={(e) => setCustomMinistry(e.target.value)}
                    placeholder="Enter custom ministry"
                    className="mt-2"
                  />
                )}
              </>
            ) : (
              <div className="text-text-primary-light dark:text-text-primary">{company?.ministry || 'N/A'}</div>
            )}
          </div>
        )}

        {/* Metrics (view mode only) */}
        {!isEditing && company && (
          <>
            <hr className="border-border-light-light dark:border-border" />
            <div>
              <h3 className="text-sm font-semibold text-text-secondary-light dark:text-text-secondary mb-2">Metrics</h3>

              {/* Legacy metrics (always shown if present) */}
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="text-text-muted-light dark:text-text-muted">Responses:</span>
                  <span className="ml-2 text-text-primary-light dark:text-text-primary font-medium">{company.responseCount}</span>
                </div>
                {company.metrics?.avgATIScore != null && (
                  <div>
                    <span className="text-text-muted-light dark:text-text-muted">ATI Score:</span>
                    <span className="ml-2 text-text-primary-light dark:text-text-primary font-medium">{company.metrics.avgATIScore.toFixed(1)}</span>
                  </div>
                )}
                {company.metrics?.adoptionRate != null && (
                  <div>
                    <span className="text-text-muted-light dark:text-text-muted">Adoption:</span>
                    <span className="ml-2 text-text-primary-light dark:text-text-primary font-medium">{company.metrics.adoptionRate.toFixed(1)}%</span>
                  </div>
                )}
                {company.metrics?.avgAIKnowledge != null && (
                  <div>
                    <span className="text-text-muted-light dark:text-text-muted">AI Knowledge:</span>
                    <span className="ml-2 text-text-primary-light dark:text-text-primary font-medium">{company.metrics.avgAIKnowledge.toFixed(1)}</span>
                  </div>
                )}
              </div>

              {/* Per-database metrics */}
              {company.databases && databases.length > 0 && Object.entries(company.databases).map(([dbId, dbData]) => {
                const db = databases.find((d) => d.id === dbId || d.slug === dbId);
                const dbMetrics = dbData.metrics || {};
                const metricKeys = Object.keys(dbMetrics);
                if (metricKeys.length === 0) return null;
                return (
                  <div key={dbId} className="mt-3 pt-3 border-t border-border-light dark:border-border">
                    <p className="text-xs font-medium text-text-muted-light dark:text-text-muted mb-2 flex items-center gap-1.5">
                      <span className="text-[11px] px-1.5 py-0 rounded font-medium bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400">
                        {db?.name || dbId}
                      </span>
                      <span>{dbData.responseCount ?? 0} responses</span>
                    </p>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      {metricKeys.slice(0, 6).map((key) => (
                        <div key={key}>
                          <span className="text-text-muted-light dark:text-text-muted">{key}:</span>
                          <span className="ml-2 text-text-primary-light dark:text-text-primary font-medium">
                            {typeof dbMetrics[key] === 'number' ? dbMetrics[key].toFixed(1) : dbMetrics[key]}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* ── Consulting Client Section ─────────────────────────────── */}
        <hr className="border-border-light dark:border-border" />
        <div>
          <p className="text-sm font-semibold text-text-primary-light dark:text-text-primary mb-3">Consulting</p>

          {/* View mode: show all consulting details */}
          {!isEditing && company?.id && (
            <div className="space-y-3">
              {/* Status + org size */}
              <div className="flex items-center gap-2">
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize ${
                  company.consultingStatus === 'active'    ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                  company.consultingStatus === 'completed' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' :
                  'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                }`}>
                  {company.consultingStatus}
                </span>
                {consulting.organisationSize && (
                  <span className="text-xs text-text-muted-light dark:text-text-muted">
                    {ORG_SIZES.find(s => s.value === consulting.organisationSize)?.label}
                  </span>
                )}
              </div>

              {/* Contacts */}
              {consulting.contacts.filter(c => c.name?.trim()).length > 0 && (
                <div>
                  <p className="text-xs font-medium text-text-secondary-light dark:text-text-secondary mb-1.5">Key Contacts</p>
                  <div className="space-y-1.5">
                    {consulting.contacts.filter(c => c.name?.trim()).map((c, i) => (
                      <div key={i} className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                        <span className="text-sm font-medium text-text-primary-light dark:text-text-primary">{c.name}</span>
                        {c.role && <span className="text-xs text-text-muted-light dark:text-text-muted">{c.role}</span>}
                        {c.email && (
                          <a href={`mailto:${c.email}`} className="text-xs text-brand-primary hover:opacity-75 transition">
                            {c.email}
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

            </div>
          )}

          {/* Edit / new mode: show full consulting setup form */}
          {isEditing && (
            <div className="space-y-4">
              {/* Status */}
              <div>
                <label className="block text-xs font-medium text-text-secondary-light dark:text-text-secondary mb-1">
                  Engagement Status
                </label>
                <Select
                  value={consulting.consultingStatus}
                  onChange={(e) => setC('consultingStatus', e.target.value)}
                  options={CONSULTING_STATUS_OPTIONS}
                />
              </div>

              {/* Organisation Size */}
              <div>
                <label className="block text-xs font-medium text-text-secondary-light dark:text-text-secondary mb-1">
                  Organisation Size
                </label>
                <Select
                  value={consulting.organisationSize}
                  onChange={(e) => setC('organisationSize', e.target.value)}
                  options={[{ value: '', label: 'Select…' }, ...ORG_SIZES]}
                />
              </div>

              {/* Contacts */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-xs font-medium text-text-secondary-light dark:text-text-secondary">
                    Key Contacts
                  </label>
                  <button onClick={addContact} className="text-xs text-brand-primary hover:opacity-75 transition flex items-center gap-1">
                    <PlusIcon className="w-3 h-3" /> Add
                  </button>
                </div>
                <div className="space-y-2">
                  {consulting.contacts.map((c, i) => (
                    <div key={i} className="grid grid-cols-3 gap-2 items-start">
                      <Input value={c.name} onChange={(e) => updateContact(i, 'name', e.target.value)} placeholder="Name" />
                      <Input value={c.role} onChange={(e) => updateContact(i, 'role', e.target.value)} placeholder="Role" />
                      <div className="flex gap-1">
                        <Input value={c.email} onChange={(e) => updateContact(i, 'email', e.target.value)} placeholder="Email" />
                        {consulting.contacts.length > 1 && (
                          <button onClick={() => removeContact(i)} className="text-danger p-1 flex-shrink-0 hover:opacity-75 transition">
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

            </div>
          )}
        </div>
      </div>
    </Card>
  );
}
